import { useCallback, useEffect, useRef, useState } from 'react';

/** Client of the public API (`/public/*`: progress, reports, replays): no token. */

export class PublicApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export async function publicFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  let res: Response;
  try {
    res = await fetch(`/public${path}`, { ...init, headers });
  } catch {
    throw new PublicApiError(0, 'network', 'We could not reach the server. Check your connection and try again.');
  }
  const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
  if (!res.ok) throw new PublicApiError(res.status, body?.error ?? 'error', body?.message ?? `Request failed with status ${res.status}.`);
  return body as T;
}

export interface PublicQuery<T> {
  data?: T;
  error?: PublicApiError;
  loading: boolean;
  reload: () => void;
}

/** Fetches `path` (null skips it); keeps the last data while reloading so polling does not flicker. */
export function usePublicQuery<T>(path: string | null): PublicQuery<T> {
  const [state, setState] = useState<{ data?: T; error?: PublicApiError; loading: boolean }>({ loading: path !== null });
  const [nonce, setNonce] = useState(0);
  const requestId = useRef(0);
  const lastPath = useRef(path);

  useEffect(() => {
    // Data of another path (e.g. another visit) is dropped; a reload of the same path keeps it.
    const samePath = lastPath.current === path;
    lastPath.current = path;
    if (path === null) {
      setState({ loading: false });
      return;
    }
    const id = ++requestId.current;
    setState((s) => ({ data: samePath ? s.data : undefined, loading: true }));
    publicFetch<T>(path)
      .then((data) => {
        if (id === requestId.current) setState({ data, loading: false });
      })
      .catch((error: PublicApiError) => {
        // A failed poll keeps the data already shown.
        if (id === requestId.current) setState((s) => ({ data: s.data, error, loading: false }));
      });
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

/** Sets the page title and the robots meta tag (public reports are never indexed). */
export function usePageMeta(title: string, options: { noindex?: boolean; description?: string } = {}) {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    // A page with its own description puts it in the head while it is open, then gives back the site's.
    const descriptionMeta = options.description ? document.querySelector<HTMLMetaElement>('meta[name="description"]') : null;
    const previousDescription = descriptionMeta?.content;
    if (descriptionMeta && options.description) descriptionMeta.content = options.description;
    let robots: HTMLMetaElement | null = null;
    if (options.noindex) {
      robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement('meta');
        robots.name = 'robots';
        document.head.appendChild(robots);
      }
      robots.content = 'noindex, nofollow';
    }
    return () => {
      document.title = previous;
      if (descriptionMeta && previousDescription !== undefined) descriptionMeta.content = previousDescription;
    };
  }, [title, options.noindex, options.description]);
}
